import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Webcam from "react-webcam";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

export default function AddProduct() {
  const navigate = useNavigate();
  const { code } = useParams();
  const isEdit = Boolean(code);

  const userRole = localStorage.getItem("role");
  const isStaff = userRole === "Staff";

  const searchParams = new URLSearchParams(window.location.search);
  const categoryFromUrl = searchParams.get("category") || "Ready Made";
  const tabFromUrl = searchParams.get("tab") || "0";

  const [formData, setFormData] = useState({
    productName: "",
    category: categoryFromUrl,
    productType: "",
    description: "",
    color: "",
    price: "",
    purchasePrice: "",
    purchaseDate: "",
    quantityInStock: "",
    unitsPerBag: "",
    weight: "",
    photo: "",
  });

  const [sizes, setSizes] = useState([
    { size: "", quantityInStock: "", materials: [] },
  ]);
  const [rawMaterials, setRawMaterials] = useState([
    { materialId: "", quantityRequired: "" },
  ]);
  const [productTypes, setProductTypes] = useState([]);
  const [availableRawMaterials, setAvailableRawMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const webcamRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState(null);

  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [originalPurchaseHistory, setOriginalPurchaseHistory] = useState([]);
  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({
    purchaseDate: new Date().toISOString().split("T")[0],
    quantityPurchased: "",
    supplierName: "",
    size: "",
    unitsPerBag: "",
    weight: "",
  });
  const [deletePurchaseDialogOpen, setDeletePurchaseDialogOpen] =
    useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);

  const normalizeProductTypeLabel = (productType) => {
    if (productType === "Salwar Suit" || productType === "Salwar Suits") {
      return "Apparels";
    }
    return productType;
  };

  const getSizeOptions = (productType) => {
    const normalizedType = normalizeProductTypeLabel(productType);
    switch (normalizedType) {
      case "Apparels":
        return ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
      case "Bedsheets":
        return ["Single", "Double", "Queen", "King"];
      case "Footwear":
        return ["5", "6", "7", "8", "9", "10", "11", "12"];
      case "Cosmetics":
      case "Earrings":
        return ["One Size"];
      case "Accessories":
        return ["One Size", "Small", "Medium", "Large"];
      case "Jewellery":
        return ["XS", "S", "M", "L", "XL", "Adjustable"];
      default:
        return ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
    }
  };

  const sizeOptions = getSizeOptions(formData.productType);

  useEffect(() => {
    fetchProductTypes();
    fetchRawMaterials();
    fetchSuppliers();
    if (isEdit) {
      fetchProductDetails();
    }
  }, [code]);

  const fetchProductTypes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/inventory/product-types`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setProductTypes(data.types || []);
      }
    } catch (error) {
      console.error("Error fetching product types:", error);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/inventory/raw-materials`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableRawMaterials(data.materials || []);
      }
    } catch (error) {
      console.error("Error fetching raw materials:", error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/suppliers/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.suppliers || []);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchProductDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/inventory/products/${code}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        const product = data.product;
        
        setFormData({
          productName: product.ProductName,
          category: product.Category,
          productType: product.ProductType || "",
          description: product.Description || "",
          color: product.Color || "",
          price: product.Price,
          purchasePrice: product.PurchasePrice || "",
          purchaseDate: product.PurchaseDate
            ? product.PurchaseDate.split("T")[0]
            : "",
          quantityInStock: product.QuantityInStock,
          unitsPerBag: product.unitsPerBag || "",
          weight: product.weight || "",
          photo: product.Photo || "",
        });

        if (product.sizes && product.sizes.length > 0) {
          setSizes(
            product.sizes.map((s) => ({
              size: s.Size,
              quantityInStock: s.QuantityInStock,
              materials:
                s.materials && s.materials.length > 0
                  ? s.materials.map((m) => ({
                      materialId: m.MaterialId,
                      quantityRequired: m.QuantityRequired,
                    }))
                  : [],
            }))
          );
        }

        if (product.rawMaterials && product.rawMaterials.length > 0) {
          setRawMaterials(
            product.rawMaterials.map((rm) => ({
              materialId: rm.MaterialId,
              quantityRequired: rm.QuantityRequired,
            }))
          );
        }

        if (product.Category === "Ready Made" && product.purchaseHistory) {
          setPurchaseHistory(product.purchaseHistory);
          setOriginalPurchaseHistory(product.purchaseHistory);
        }
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error("Failed to load product details");
    }
  };

  const uploadPhotoFile = async (file, productCode = null) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("photo", file);
      if (productCode) {
        formDataUpload.append("productCode", productCode);
      }
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/inventory/upload-photo`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataUpload,
        }
      );
      if (response.ok) {
        const data = await response.json();
        return data.photoUrl;
      } else {
        throw new Error("Failed to upload photo");
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      setPendingPhotoFile(file);
      setPendingPhotoPreview(URL.createObjectURL(file));
    } else if (file) {
      toast.error("File size must be less than 5MB");
    }
    e.target.value = "";
  };

  const handleCapturePhoto = () => {
    const image = webcamRef.current?.getScreenshot();
    if (image) {
      setCapturedPhoto(image);
    }
  };

  const handleUploadCapturedPhoto = async () => {
    if (!capturedPhoto) return;

    const response = await fetch(capturedPhoto);
    const blob = await response.blob();
    const file = new File([blob], "product-camera-photo.jpg", {
      type: "image/jpeg",
    });
    setPendingPhotoFile(file);
    setPendingPhotoPreview(capturedPhoto);
    setCapturedPhoto(null);
    setCameraOpen(false);
  };
  const handleAddPurchase = () => {
    if (
      !purchaseForm.quantityPurchased ||
      !purchaseForm.purchaseDate ||
      !purchaseForm.supplierName
    ) {
      toast.error(
        "Please fill in all required fields including supplier name"
      );
      return;
    }

    const purchasePrice =
      formData.purchasePrice && formData.purchasePrice !== ""
        ? formData.purchasePrice
        : formData.price;
    const totalCost =
      parseFloat(purchaseForm.quantityPurchased) *
      parseFloat(purchasePrice);

    if (editingPurchase) {
      const updatedHistory = purchaseHistory.map((purchase) =>
        purchase.PurchaseId === editingPurchase.PurchaseId
          ? {
              ...purchase,
              PurchaseDate: purchaseForm.purchaseDate,
              QuantityPurchased: parseFloat(purchaseForm.quantityPurchased),
              SupplierName: purchaseForm.supplierName,
              Size: purchaseForm.size,
              UnitsPerBag: purchaseForm.unitsPerBag,
              Weight: purchaseForm.weight,
              TotalCost: totalCost,
            }
          : purchase
      );
      setPurchaseHistory(updatedHistory);
    } else {
      const newPurchase = {
        PurchaseId: Date.now(),
        ProductCode: code,
        PurchaseDate: purchaseForm.purchaseDate,
        QuantityPurchased: parseFloat(purchaseForm.quantityPurchased),
        SupplierName: purchaseForm.supplierName,
        Size: purchaseForm.size,
        UnitsPerBag: purchaseForm.unitsPerBag,
        Weight: purchaseForm.weight,
        TotalCost: totalCost,
        isNew: true,
      };
      setPurchaseHistory([...purchaseHistory, newPurchase]);
    }

    setOpenPurchaseDialog(false);
    setEditingPurchase(null);
    setPurchaseForm({
      purchaseDate: new Date().toISOString().split("T")[0],
      quantityPurchased: "",
      supplierName: "",
      size: "",
      unitsPerBag: "",
      weight: "",
    });
  };

  const handleEditPurchase = (purchase) => {
    setEditingPurchase(purchase);
    setPurchaseForm({
      purchaseDate: purchase.PurchaseDate.split("T")[0],
      quantityPurchased: purchase.QuantityPurchased,
      supplierName: purchase.SupplierName || "",
      size: purchase.Size || "",
      unitsPerBag: purchase.UnitsPerBag || "",
      weight: purchase.Weight || "",
    });
    setOpenPurchaseDialog(true);
  };

  const handleDeletePurchaseClick = (purchase) => {
    setPurchaseToDelete(purchase);
    setDeletePurchaseDialogOpen(true);
  };

  const handleDeletePurchaseConfirm = () => {
    if (!purchaseToDelete) return;

    const updatedHistory = purchaseHistory.filter(
      (purchase) => purchase.PurchaseId !== purchaseToDelete.PurchaseId
    );
    setPurchaseHistory(updatedHistory);

    setDeletePurchaseDialogOpen(false);
    setPurchaseToDelete(null);
  };

  const handleDeletePurchaseCancel = () => {
    setDeletePurchaseDialogOpen(false);
    setPurchaseToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.productName || !formData.price) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const endpoint = isEdit
        ? `${API_BASE_URL}/api/inventory/products/${code}`
        : `${API_BASE_URL}/api/inventory/products`;
      let photo = formData.photo;
      if (pendingPhotoFile) {
        photo = await uploadPhotoFile(pendingPhotoFile, isEdit ? code : null);
        if (!photo) return;
      }

      const payload = {
        ...formData,
        photo,
        unitsPerBag: formData.unitsPerBag || null,
        weight: formData.weight || null,
        sizes: sizes.filter((s) => s.size),
        rawMaterials:
          formData.category === "Customized"
            ? rawMaterials.filter(
                (rm) => rm.materialId && rm.quantityRequired
              )
            : [],
      };
      const response = await fetch(endpoint, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const responseData = await response.json();
        if (
          formData.category === "Ready Made" &&
          (purchaseHistory.length > 0 || originalPurchaseHistory.length > 0)
        ) {
          const productCode = isEdit ? code : responseData.productCode;
          await handlePurchaseHistoryUpdates(token, productCode);
        }
        toast.success(`Product ${isEdit ? "updated" : "created"} successfully`);
        navigate(`/inventory?tab=${tabFromUrl}`);
      } else {
        const data = await response.json();
        toast.error(data.message || "Failed to save product");
      }
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(error.message || "Failed to save product");
    }
  };

  const handlePurchaseHistoryUpdates = async (token, productCode = code) => {
    const currentPurchaseIds = purchaseHistory.map((p) => p.PurchaseId);
    const deletedPurchases = originalPurchaseHistory.filter(
      (original) => !currentPurchaseIds.includes(original.PurchaseId)
    );

    for (const purchase of deletedPurchases) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/inventory/purchases/products/${purchase.PurchaseId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Delete failed (${response.status})`);
        }
      } catch (error) {
        console.error("Error deleting purchase:", error);
        throw new Error(`Purchase history delete failed: ${error.message}`);
      }
    }

    const newPurchases = purchaseHistory.filter((purchase) => purchase.isNew);

    for (const purchase of newPurchases) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/inventory/purchases/products`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              productCode: productCode,
              purchaseDate: purchase.PurchaseDate,
              quantityPurchased: purchase.QuantityPurchased,
              supplierName: purchase.SupplierName,
              size: purchase.Size,
              unitsPerBag: purchase.UnitsPerBag,
              weight: purchase.Weight,
              paymentStatus: "Paid",
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Failed to add purchase:", errorData);
          throw new Error(errorData.message || `Add failed (${response.status})`);
        }
      } catch (error) {
        console.error("Error adding purchase:", error);
        throw new Error(`Purchase history add failed: ${error.message}`);
      }
    }

    const updatedPurchases = purchaseHistory.filter(
      (purchase) => !purchase.isNew && purchase.PurchaseId
    );

    for (const purchase of updatedPurchases) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/inventory/purchases/products/${purchase.PurchaseId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              purchaseDate: purchase.PurchaseDate,
              quantityPurchased: purchase.QuantityPurchased,
              supplierName: purchase.SupplierName,
              size: purchase.Size,
              unitsPerBag: purchase.UnitsPerBag,
              weight: purchase.Weight,
            }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Update failed (${response.status})`);
        }
      } catch (error) {
        console.error("Error updating purchase:", error);
        throw new Error(`Purchase history update failed: ${error.message}`);
      }
    }
  };

  const addSize = () => {
    setSizes([
      ...sizes,
      { size: "", quantityInStock: "", materials: [] },
    ]);
  };

  const removeSize = (index) => {
    setSizes(sizes.filter((_, i) => i !== index));
  };

  const updateSize = (index, field, value) => {
    const newSizes = [...sizes];
    newSizes[index][field] = value;
    setSizes(newSizes);
  };

  const addMaterialToSize = (sizeIndex) => {
    const newSizes = [...sizes];
    if (!newSizes[sizeIndex].materials) {
      newSizes[sizeIndex].materials = [];
    }
    newSizes[sizeIndex].materials.push({
      materialId: "",
      quantityRequired: "",
    });
    setSizes(newSizes);
  };

  const removeMaterialFromSize = (sizeIndex, materialIndex) => {
    const newSizes = [...sizes];
    newSizes[sizeIndex].materials = newSizes[sizeIndex].materials.filter(
      (_, i) => i !== materialIndex
    );
    setSizes(newSizes);
  };

  const updateSizeMaterial = (sizeIndex, materialIndex, field, value) => {
    const newSizes = [...sizes];
    newSizes[sizeIndex].materials[materialIndex][field] = value;
    setSizes(newSizes);
  };

  const addRawMaterial = () => {
    setRawMaterials([
      ...rawMaterials,
      { materialId: "", quantityRequired: "" },
    ]);
  };

  const removeRawMaterial = (index) => {
    setRawMaterials(rawMaterials.filter((_, i) => i !== index));
  };

  const updateRawMaterial = (index, field, value) => {
    const newMaterials = [...rawMaterials];
    newMaterials[index][field] = value;
    setRawMaterials(newMaterials);
  };

  // Dark Theme Styles
  const darkTextField = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#3a3a3a",
      borderRadius: 1,
      "& fieldset": {
        borderColor: "rgba(196, 165, 116, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(196, 165, 116, 0.4)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#C4A574",
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "#fff",
      "&::placeholder": {
        color: "rgba(196, 165, 116, 0.5)",
        opacity: 1,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "#C4A574",
    },
  };

  const darkSelect = {
    backgroundColor: "#3a3a3a",
    color: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(196, 165, 116, 0.2)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(196, 165, 116, 0.4)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#C4A574",
    },
    "& .MuiSvgIcon-root": {
      color: "#C4A574",
    },
  };
  return (
    <AppLayout>
      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          p: { xs: 2, md: 4 },
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
        }}
      >
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/inventory?tab=${tabFromUrl}`)}
          sx={{
            color: "#C4A574",
            mb: 3,
            fontSize: "0.95rem",
            "&:hover": { bgcolor: "rgba(196, 165, 116, 0.1)" },
          }}
        >
          Back to Inventory
        </Button>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            letterSpacing: 3,
            color: "#BC965E",
            textAlign: "center",
            mb: 4,
            textDecoration: "underline",
            textUnderlineOffset: 12,
            textDecorationThickness: 2,
          }}
        >
          {isEdit ? "EDIT PRODUCT" : "ADD NEW PRODUCT"}
        </Typography>

        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <form onSubmit={handleSubmit}>
            {/* BASIC INFORMATION */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 4 },
                mb: 3,
                backgroundColor: "#2a2a2a",
                border: "1px solid rgba(196, 165, 116, 0.2)",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: "#C4A574",
                  fontSize: "1rem",
                  letterSpacing: 0.5,
                }}
              >
                Basic Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      color: "#C4A574",
                      fontWeight: 700,
                      mb: 1,
                      fontSize: "0.9rem",
                    }}
                  >
                    Product Name *
                  </Typography>
                  <TextField
                    placeholder="Enter product name"
                    value={formData.productName}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        productName: e.target.value,
                      })
                    }
                    fullWidth
                    required
                    sx={darkTextField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      color: "#C4A574",
                      fontWeight: 700,
                      mb: 1,
                      fontSize: "0.9rem",
                    }}
                  >
                    Category
                  </Typography>
                  <TextField
                    value={formData.category}
                    fullWidth
                    disabled
                    sx={darkTextField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      color: "#C4A574",
                      fontWeight: 700,
                      mb: 1,
                      fontSize: "0.9rem",
                    }}
                  >
                    Product Type
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.productType}
                      onChange={(e) => {
                        const newProductType = e.target.value;
                        setFormData({
                          ...formData,
                          productType: newProductType,
                        });

                        if (newProductType !== formData.productType) {
                          setSizes([
                            {
                              size: "",
                              quantityInStock: "",
                              materials: [],
                            },
                          ]);
                        }
                      }}
                      sx={darkSelect}
                    >
                      <MenuItem value="">None</MenuItem>
                      {productTypes.map((type) => (
                        <MenuItem
                          key={type.ProductTypeId}
                          value={type.TypeName}
                        >
                          {normalizeProductTypeLabel(type.TypeName)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      color: "#C4A574",
                      fontWeight: 700,
                      mb: 1,
                      fontSize: "0.9rem",
                    }}
                  >
                    Color
                  </Typography>
                  <TextField
                    placeholder="Enter color"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    fullWidth
                    sx={darkTextField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      color: "#C4A574",
                      fontWeight: 700,
                      mb: 1,
                      fontSize: "0.9rem",
                    }}
                  >
                    Price (₹) *
                  </Typography>
                  <TextField
                    placeholder="Enter selling price"
                    type="number"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    fullWidth
                    required
                    sx={darkTextField}
                  />
                </Grid>

                {formData.category !== "Customized" && (
                  <Grid item xs={12} md={6}>
                    <Typography
                      sx={{
                        color: "#C4A574",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Purchase Price (₹)
                    </Typography>
                    <TextField
                      placeholder="Enter purchase price"
                      type="number"
                      value={formData.purchasePrice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          purchasePrice: e.target.value,
                        })
                      }
                      fullWidth
                      helperText="Cost price at which this product was purchased"
                      sx={darkTextField}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography
                    sx={{
                      color: "#C4A574",
                      fontWeight: 700,
                      mb: 1,
                      fontSize: "0.9rem",
                    }}
                  >
                    Description
                  </Typography>
                  <TextField
                    placeholder="Enter product description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description: e.target.value,
                      })
                    }
                    fullWidth
                    multiline
                    rows={3}
                    sx={darkTextField}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    sx={{
                      color: "#C4A574",
                      fontWeight: 700,
                      mb: 2,
                      fontSize: "0.9rem",
                    }}
                  >
                    Product Photo
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="photo-upload"
                    type="file"
                    onChange={handlePhotoUpload}
                  />
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <label htmlFor="photo-upload" style={{ flex: "1 1 220px" }}>
                      <Button
                        variant="outlined"
                        component="span"
                        fullWidth
                        disabled={uploading}
                        sx={{
                          py: 1.5,
                          borderStyle: "dashed",
                          borderWidth: 2,
                          borderColor: "#C4A574",
                          color: "#C4A574",
                          fontWeight: 600,
                          "&:hover": {
                            borderColor: "#B39565",
                            backgroundColor: "rgba(196, 165, 116, 0.1)",
                          },
                        }}
                      >
                        {uploading ? <CircularProgress size={24} sx={{ color: "#C4A574" }} /> : pendingPhotoPreview || formData.photo ? "Change Photo" : "Upload Photo"}
                      </Button>
                    </label>
                    <Button
                      variant="outlined"
                      startIcon={<CameraAltIcon />}
                      onClick={() => { setCapturedPhoto(null); setCameraOpen(true); }}
                      disabled={uploading}
                      sx={{
                        flex: "1 1 220px",
                        py: 1.5,
                        borderColor: "#C4A574",
                        color: "#C4A574",
                        fontWeight: 600,
                        "&:hover": {
                          borderColor: "#B39565",
                          backgroundColor: "rgba(196, 165, 116, 0.1)",
                        },
                      }}
                    >
                      Use Camera
                    </Button>
                  </Box>
                  {(pendingPhotoPreview || formData.photo) && (
                    <Box sx={{ mt: 3, textAlign: "center" }}>
                      <img
                        src={pendingPhotoPreview || `${API_BASE_URL}${formData.photo}`}
                        alt="Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          borderRadius: "8px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>
            {/* PURCHASE HISTORY MANAGEMENT */}
            {!isStaff && formData.category === "Ready Made" && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 4 },
                  mb: 3,
                  backgroundColor: "#2a2a2a",
                  border: "1px solid rgba(196, 165, 116, 0.2)",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 2, sm: 0 },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#C4A574",
                      fontSize: "1rem",
                      letterSpacing: 0.5,
                    }}
                  >
                    Purchase History Management
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingPurchase(null);
                      setPurchaseForm({
                        purchaseDate: new Date()
                          .toISOString()
                          .split("T")[0],
                        quantityPurchased: "",
                        totalCost: "",
                        supplierName: "",
                        size: "",
                        unitsPerBag: "",
                        weight: "",
                      });
                      setOpenPurchaseDialog(true);
                    }}
                    sx={{
                      bgcolor: "#C4A574",
                      color: "#000",
                      fontWeight: 700,
                      "&:hover": { bgcolor: "#B39565" },
                    }}
                  >
                    Add Purchase
                  </Button>
                </Box>

                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: "#999" }}
                >
                  Manage purchase records to track stock quantities and sizes.
                  Changes will be saved when you click "
                  {isEdit ? "Update" : "Create"} Product" below.
                </Typography>

                {(purchaseHistory.some((p) => p.isNew) ||
                  purchaseHistory.length !==
                    originalPurchaseHistory.length ||
                  JSON.stringify(
                    purchaseHistory.filter((p) => !p.isNew)
                  ) !==
                    JSON.stringify(originalPurchaseHistory)) && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "rgba(255, 193, 7, 0.1)",
                      border: "1px solid rgba(255, 193, 7, 0.3)",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#FFD700",
                        fontWeight: 600,
                      }}
                    >
                      ⚠️ You have unsaved changes to purchase history. Click "
                      {isEdit ? "Update" : "Create"} Product" to save all
                      changes.
                    </Typography>
                  </Box>
                )}

                {/* Mobile Card View (xs screens) */}
                <Box sx={{ display: { xs: "block", sm: "none" } }}>
                  {purchaseHistory.length > 0 ? (
                    purchaseHistory.map((purchase) => (
                      <Card
                        key={purchase.PurchaseId}
                        sx={{
                          mb: 2,
                          backgroundColor: "#3a3a3a",
                          border: "1px solid rgba(196, 165, 116, 0.2)",
                          borderRadius: 2,
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            {/* Avatar with Date Initial */}
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor: "#C4A574",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#000",
                                fontWeight: 700,
                                fontSize: "0.9rem",
                                mr: 2,
                              }}
                            >
                              {new Date(purchase.PurchaseDate).getDate()}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ color: "#C4A574", fontWeight: 700, fontSize: "0.95rem" }}>
                                Purchase #{purchase.PurchaseId}
                              </Typography>
                              <Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>
                                {purchase.SupplierName || "Unknown Supplier"}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Typography sx={{ color: "#999", fontSize: "0.8rem", mb: 2 }}>
                            {new Date(purchase.PurchaseDate).toLocaleDateString()}
                          </Typography>
                          
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box>
                              <Typography sx={{ color: "#fff", fontSize: "0.75rem" }}>
                                Size: {purchase.Size || "-"} | Qty: {purchase.QuantityPurchased}
                              </Typography>
                              <Typography sx={{ color: "#C4A574", fontSize: "0.8rem", fontWeight: 600 }}>
                                ₹{parseFloat(purchase.TotalCost).toFixed(2)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <IconButton
                                onClick={() => handleEditPurchase(purchase)}
                                sx={{ 
                                  backgroundColor: "rgba(196, 165, 116, 0.1)",
                                  color: "#C4A574", 
                                  p: 0.5,
                                  "&:hover": { backgroundColor: "rgba(196, 165, 116, 0.2)" }
                                }}
                                size="small"
                              >
                                <EditIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDeletePurchaseClick(purchase)}
                                sx={{ 
                                  backgroundColor: "rgba(255, 107, 107, 0.1)",
                                  color: "#ff6b6b", 
                                  p: 0.5,
                                  "&:hover": { backgroundColor: "rgba(255, 107, 107, 0.2)" }
                                }}
                                size="small"
                              >
                                <DeleteIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card sx={{ backgroundColor: "#3a3a3a", border: "1px solid rgba(196, 165, 116, 0.2)" }}>
                      <CardContent sx={{ textAlign: "center", py: 4 }}>
                        <Typography sx={{ color: "#999" }}>
                          No purchase history yet. Add your first purchase record to start tracking stock!
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Box>

                {/* Desktop Table View (sm+ screens) */}
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  <TableContainer sx={{ borderRadius: 2, overflow: "auto" }}>
                    <Table sx={{ minWidth: 500 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#C4A574" }}>
                          <TableCell sx={{ color: "#000", fontWeight: 700, fontSize: "0.85rem" }}>Date</TableCell>
                          <TableCell sx={{ color: "#000", fontWeight: 700, fontSize: "0.85rem" }}>Supplier</TableCell>
                          <TableCell sx={{ color: "#000", fontWeight: 700, fontSize: "0.85rem" }}>Size</TableCell>
                          <TableCell sx={{ color: "#000", fontWeight: 700, fontSize: "0.85rem" }}>Qty</TableCell>
                          <TableCell sx={{ color: "#000", fontWeight: 700, fontSize: "0.85rem" }}>Units/Bag</TableCell>
                          <TableCell sx={{ color: "#000", fontWeight: 700, fontSize: "0.85rem" }}>Weight (in grams)</TableCell>
                          <TableCell sx={{ color: "#000", fontWeight: 700, fontSize: "0.85rem" }}>Total</TableCell>
                          <TableCell align="center" sx={{ color: "#000", fontWeight: 700, fontSize: "0.85rem" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {purchaseHistory.length > 0 ? (
                          purchaseHistory.map((purchase) => (
                            <TableRow key={purchase.PurchaseId} sx={{ backgroundColor: "#3a3a3a", "&:hover": { backgroundColor: "#444" } }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#fff" }}>
                                {new Date(purchase.PurchaseDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.85rem", color: "#fff" }}>
                                {purchase.SupplierName || "-"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.85rem", color: "#fff" }}>
                                {purchase.Size || "-"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.85rem", color: "#fff" }}>
                                {purchase.QuantityPurchased}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.85rem", color: "#fff" }}>
                                {purchase.UnitsPerBag || "-"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.85rem", color: "#fff" }}>
                                {purchase.Weight || "-"}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#C4A574" }}>
                                ₹{parseFloat(purchase.TotalCost).toFixed(2)}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  onClick={() => handleEditPurchase(purchase)}
                                  sx={{ color: "#C4A574", p: 0.25 }}
                                  size="small"
                                >
                                  <EditIcon sx={{ fontSize: "1rem" }} />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDeletePurchaseClick(purchase)}
                                  sx={{ color: "#ff6b6b", p: 0.25 }}
                                  size="small"
                                >
                                  <DeleteIcon sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow sx={{ backgroundColor: "#3a3a3a" }}>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              <Typography sx={{ color: "#999" }}>
                                No purchase history yet. Add your first purchase record to start tracking stock!
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            )}
            {/* SIZE VARIANTS */}
            {formData.category === "Customized" && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 4 },
                  mb: 3,
                  backgroundColor: "#2a2a2a",
                  border: "1px solid rgba(196, 165, 116, 0.2)",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 2, sm: 0 },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#C4A574",
                      fontSize: "1rem",
                      letterSpacing: 0.5,
                    }}
                  >
                    Size Table
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addSize}
                    variant="outlined"
                    sx={{
                      borderColor: "#C4A574",
                      color: "#C4A574",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "rgba(196, 165, 116, 0.1)",
                        borderColor: "#C4A574",
                      },
                    }}
                  >
                    Add Size
                  </Button>
                </Box>

                {sizes.map((size, sizeIndex) => (
                  <Card
                    key={sizeIndex}
                    sx={{
                      mb: 3,
                      backgroundColor: "#3a3a3a",
                      border: "1px solid rgba(196, 165, 116, 0.2)",
                      borderRadius: 2,
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#C4A574",
                          }}
                        >
                          Size #{sizeIndex + 1}
                        </Typography>
                        {sizes.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => removeSize(sizeIndex)}
                            sx={{ color: "#ff6b6b" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            sx={{
                              color: "#C4A574",
                              fontWeight: 700,
                              mb: 1,
                              fontSize: "0.9rem",
                            }}
                          >
                            Size *
                          </Typography>
                          <FormControl fullWidth>
                            <Select
                              value={size.size}
                              onChange={(e) =>
                                updateSize(
                                  sizeIndex,
                                  "size",
                                  e.target.value
                                )
                              }
                              sx={darkSelect}
                            >
                              {getSizeOptions(
                                formData.productType
                              ).map((s) => (
                                <MenuItem key={s} value={s}>
                                  {s}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            sx={{
                              color: "#C4A574",
                              fontWeight: 700,
                              mb: 1,
                              fontSize: "0.9rem",
                            }}
                          >
                            Quantity in Stock
                          </Typography>
                          <TextField
                            placeholder="Enter quantity"
                            type="number"
                            value={size.quantityInStock}
                            onChange={(e) =>
                              updateSize(
                                sizeIndex,
                                "quantityInStock",
                                e.target.value
                              )
                            }
                            fullWidth
                            sx={darkTextField}
                          />
                        </Grid>
                      </Grid>

                      {formData.category === "Customized" && (
                        <Box
                          sx={{
                            mt: 3,
                            p: 2,
                            backgroundColor: "rgba(196, 165, 116, 0.1)",
                            borderRadius: 2,
                            border: "1px solid rgba(196, 165, 116, 0.2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                              flexDirection: { xs: "column", sm: "row" },
                              gap: { xs: 1, sm: 0 },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#C4A574",
                              }}
                            >
                              Raw Materials for{" "}
                              {size.size || "this size"}
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() =>
                                addMaterialToSize(sizeIndex)
                              }
                              variant="outlined"
                              sx={{
                                borderColor: "#C4A574",
                                color: "#C4A574",
                                "&:hover": {
                                  borderColor: "#C4A574",
                                  backgroundColor:
                                    "rgba(196, 165, 116, 0.2)",
                                },
                              }}
                            >
                              Add Material
                            </Button>
                          </Box>

                          {size.materials &&
                          size.materials.length > 0 ? (
                            size.materials.map((material, matIndex) => (
                              <Grid
                                container
                                spacing={2}
                                key={matIndex}
                                sx={{ mb: 2 }}
                              >
                                <Grid item xs={12} sm={5}>
                                  <FormControl
                                    fullWidth
                                    size="small"
                                  >
                                    <Select
                                      value={material.materialId}
                                      onChange={(e) =>
                                        updateSizeMaterial(
                                          sizeIndex,
                                          matIndex,
                                          "materialId",
                                          e.target.value
                                        )
                                      }
                                      sx={darkSelect}
                                    >
                                      {availableRawMaterials.map(
                                        (rm) => (
                                          <MenuItem
                                            key={
                                              rm.MaterialId
                                            }
                                            value={
                                              rm.MaterialId
                                            }
                                          >
                                            {rm.MaterialName}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                  <TextField
                                    placeholder="Quantity Required"
                                    type="number"
                                    value={
                                      material.quantityRequired
                                    }
                                    onChange={(e) =>
                                      updateSizeMaterial(
                                        sizeIndex,
                                        matIndex,
                                        "quantityRequired",
                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                    size="small"
                                    sx={darkTextField}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                  {size.materials.length > 1 && (
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        removeMaterialFromSize(
                                          sizeIndex,
                                          matIndex
                                        )
                                      }
                                      sx={{ color: "#ff6b6b" }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Grid>
                            ))
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#999",
                                fontStyle: "italic",
                                textAlign: "center",
                                py: 2,
                              }}
                            >
                              No materials added yet. Click
                              "Add Material" to specify raw
                              materials for this size.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Paper>
            )}

            {/* READY MADE PRODUCT INFO */}
            {formData.category === "Ready Made" && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 4 },
                  mb: 3,
                  backgroundColor: "rgba(196, 165, 116, 0.1)",
                  border: "1px solid rgba(196, 165, 116, 0.2)",
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#C4A574",
                    mb: 2,
                    fontSize: "1rem",
                  }}
                >
                  Ready-Made Product Information
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#999", mb: 2 }}
                >
                  For ready-made products, sizes and stock
                  quantities are managed through purchase history
                  records.
                </Typography>
                <Typography variant="body2" sx={{ color: "#777" }}>
                  After creating this product, you can add
                  purchase records in the edit mode to track
                  different sizes and quantities.
                </Typography>
              </Paper>
            )}

            {/* ACTION BUTTONS */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(`/inventory?tab=${tabFromUrl}`)
                }
                size="large"
                sx={{
                  px: 4,
                  borderColor: "#C4A574",
                  color: "#C4A574",
                  fontWeight: 700,
                  "&:hover": {
                    backgroundColor: "rgba(196, 165, 116, 0.1)",
                    borderColor: "#C4A574",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                size="large"
                sx={{
                  px: 4,
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#B39565" },
                }}
              >
                {isEdit ? "Update Product" : "Create Product"}
              </Button>
            </Box>
          </form>

          {/* PURCHASE MANAGEMENT DIALOGS */}
          <Dialog
            open={openPurchaseDialog}
            onClose={() => setOpenPurchaseDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: "#2a2a2a",
                backgroundImage: "none",
              },
            }}
          >
            <DialogTitle sx={{ fontWeight: 700, color: "#C4A574" }}>
              {editingPurchase
                ? "Edit Purchase Record"
                : "Add Purchase Record"}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        color: "#C4A574",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Purchase Date *
                    </Typography>
                    <TextField
                      type="date"
                      value={purchaseForm.purchaseDate}
                      onChange={(e) =>
                        setPurchaseForm({
                          ...purchaseForm,
                          purchaseDate: e.target.value,
                        })
                      }
                      fullWidth
                      required
                      slotProps={{
                        inputLabel: { shrink: true },
                      }}
                      sx={darkTextField}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      sx={{
                        color: "#C4A574",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Size
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={purchaseForm.size}
                        onChange={(e) =>
                          setPurchaseForm({
                            ...purchaseForm,
                            size: e.target.value,
                          })
                        }
                        sx={darkSelect}
                      >
                        <MenuItem value="">All Sizes</MenuItem>
                        {sizeOptions.map((size) => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      sx={{
                        color: "#C4A574",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Quantity *
                    </Typography>
                    <TextField
                      placeholder="Enter quantity"
                      type="number"
                      value={purchaseForm.quantityPurchased}
                      onChange={(e) =>
                        setPurchaseForm({
                          ...purchaseForm,
                          quantityPurchased: e.target.value,
                        })
                      }
                      fullWidth
                      required
                      sx={darkTextField}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      sx={{
                        color: "#C4A574",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Units Per Bag
                    </Typography>
                    <TextField
                      placeholder="Units per bag"
                      type="number"
                      value={purchaseForm.unitsPerBag}
                      onChange={(e) =>
                        setPurchaseForm({
                          ...purchaseForm,
                          unitsPerBag: e.target.value,
                        })
                      }
                      fullWidth
                      sx={darkTextField}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      sx={{
                        color: "#C4A574",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Weight (in grams)
                    </Typography>
                    <TextField
                      placeholder="Weight (in grams)"
                      value={purchaseForm.weight}
                      onChange={(e) =>
                        setPurchaseForm({
                          ...purchaseForm,
                          weight: e.target.value,
                        })
                      }
                      fullWidth
                      sx={darkTextField}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        color: "#C4A574",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Total Cost (₹)
                    </Typography>
                    <TextField
                      type="number"
                      value={
                        purchaseForm.quantityPurchased
                          ? (() => {
                              const quantity = parseFloat(
                                purchaseForm.quantityPurchased ||
                                  0
                              );
                              const unitPrice =
                                formData.purchasePrice &&
                                formData.purchasePrice !== ""
                                  ? parseFloat(
                                      formData.purchasePrice
                                    )
                                  : parseFloat(
                                      formData.price || 0
                                    );
                              return (quantity * unitPrice).toFixed(
                                2
                              );
                            })()
                          : "0.00"
                      }
                      fullWidth
                      disabled
                      helperText={`Automatically calculated: Quantity × ${
                        formData.purchasePrice &&
                        formData.purchasePrice !== ""
                          ? "Purchase Price (₹" +
                            formData.purchasePrice +
                            ")"
                          : "Selling Price (₹" +
                            (formData.price || "0") +
                            ") - Please set Purchase Price"
                      }`}
                      sx={{
                        ...darkTextField,
                        "& .MuiFormHelperText-root": {
                          color: "#999",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        color: "#C4A574",
                        fontWeight: 700,
                        mb: 1,
                        fontSize: "0.9rem",
                      }}
                    >
                      Supplier Name *
                    </Typography>
                    <FormControl fullWidth required>
                      <Select
                        value={purchaseForm.supplierName}
                        onChange={(e) =>
                          setPurchaseForm({
                            ...purchaseForm,
                            supplierName: e.target.value,
                          })
                        }
                        required
                        sx={darkSelect}
                      >
                        {suppliers.map((supplier) => (
                          <MenuItem
                            key={supplier.SupplierId}
                            value={supplier.SupplierCode}
                          >
                            {supplier.SupplierCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: "1px solid rgba(196, 165, 116, 0.2)" }}>
              <Button
                onClick={() => setOpenPurchaseDialog(false)}
                sx={{ color: "#C4A574" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddPurchase}
                variant="contained"
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#B39565" },
                }}
              >
                {editingPurchase ? "Update" : "Add"} Purchase
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={cameraOpen}
            onClose={() => { setCameraOpen(false); setCapturedPhoto(null); }}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { backgroundColor: "#2a2a2a", backgroundImage: "none" } }}
          >
            <DialogTitle sx={{ color: "#C4A574", fontWeight: 700 }}>
              {capturedPhoto ? "Preview Product Photo" : "Take Product Photo"}
            </DialogTitle>
            <DialogContent sx={{ textAlign: "center" }}>
              {capturedPhoto ? (
                <img
                  src={capturedPhoto}
                  alt="Captured product preview"
                  style={{ width: "100%", maxHeight: "55vh", objectFit: "contain", borderRadius: 8 }}
                />
              ) : (
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{ facingMode: "environment" }}
                  style={{ width: "100%", borderRadius: 8 }}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ borderTop: "1px solid rgba(196, 165, 116, 0.2)" }}>
              {capturedPhoto ? (
                <>
                  <Button onClick={() => setCapturedPhoto(null)} sx={{ color: "#C4A574" }}>
                    Retake
                  </Button>
                  <Button onClick={handleUploadCapturedPhoto} variant="contained" disabled={uploading} sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 700 }}>
                    {uploading ? <CircularProgress size={22} sx={{ color: "#000" }} /> : "Upload"}
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => setCameraOpen(false)} sx={{ color: "#C4A574" }}>
                    Cancel
                  </Button>
                  <Button onClick={handleCapturePhoto} variant="contained" sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 700 }}>
                    Take Photo
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>

          {/* DELETE PURCHASE CONFIRMATION DIALOG */}
          <Dialog
            open={deletePurchaseDialogOpen}
            onClose={handleDeletePurchaseCancel}
            PaperProps={{
              sx: {
                backgroundColor: "#2a2a2a",
                backgroundImage: "none",
              },
            }}
          >
            <DialogTitle sx={{ color: "#fff" }}>
              Confirm Delete
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ color: "#fff" }}>
                Are you sure you want to delete this purchase
                record from{" "}
                {purchaseToDelete &&
                  new Date(
                    purchaseToDelete.PurchaseDate
                  ).toLocaleDateString()}
                ? This will affect the calculated stock quantities
                and cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ borderTop: "1px solid rgba(196, 165, 116, 0.2)" }}>
              <Button
                onClick={handleDeletePurchaseCancel}
                sx={{ color: "#C4A574" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeletePurchaseConfirm}
                variant="contained"
                sx={{
                  bgcolor: "#ff6b6b",
                  "&:hover": { bgcolor: "#ff5252" },
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* Decorative Fade */}
        <Box
          sx={{
            mt: 6,
            height: 60,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.3))",
            borderRadius: 2,
          }}
        />
      </Box>
    </AppLayout>
  );
}